import endpoints from '@app/src/api/endpoints'
import { FetchKey, useFetchResource } from '@app/src/api/fetchHooks'
import { useUpdateResource } from '@app/src/api/updateHooks'
import { useSnackbar } from '@app/src/context/SnackbarContext'
import useErrorNotification from '@app/src/hooks/errorNotification'
import type { EntitlementChange, OrganizationEntitlementOverview } from '@app/src/types/entitlements'
import { NotificationSeverity } from '@app/src/wf-constants'
import { useQueryClient } from '@tanstack/react-query'

export const useOrganizationEntitlements = (organizationId: number, solutionType: string) => {
  const queryClient = useQueryClient()
  const { showSnackbar } = useSnackbar()
  const { showErrorNotification } = useErrorNotification()
  const { mutate, isPending: isSaving } = useUpdateResource<unknown, { changes: EntitlementChange[] }>()

  const { data: overview, isLoading } = useFetchResource<OrganizationEntitlementOverview>({
    key: [FetchKey.OrganizationEntitlements, organizationId, solutionType],
    endpoint: endpoints.organizationEntitlementsBySolution(organizationId, solutionType),
    options: { enabled: Boolean(solutionType) },
  })

  const save = (changes: EntitlementChange[], successMessage: string, onSaved?: () => void) =>
    mutate(
      { url: endpoints.organizationEntitlements(organizationId), body: { changes } },
      {
        onError: error => showErrorNotification({ requestError: error }),
        onSuccess: () => {
          queryClient.invalidateQueries({ queryKey: [FetchKey.OrganizationEntitlements, organizationId] })
          showSnackbar({ message: successMessage, severity: NotificationSeverity.success })
          onSaved?.()
        },
      },
    )

  return { overview, isLoading, save, isSaving }
}
